import { useCallback, useEffect, useMemo } from 'react';

import { getMonths } from 'utilitys/helpers/Date/dateHelper';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useProgramCriticalAnalysisStore } from 'store/programCriticalAnalysis.store';

import { IActivitiesData } from 'api/queries/getDelayDriver.query';

export const useRenderCriticalAnalysisDelayDriversLogic = ({ title, subTitle, api, ui, table }) => {
    const globalDateFilter = useProgramCriticalAnalysisStore((store) => store.dateFilter);

    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();
    const { setOnSearch, setIsDelayDriver, setStatusFilter, setTypeFilter, setIsCriticalPath, setDateFilter } =
        ui.setters;

    // handlers
    const handleAPICsvExport = () => {
        if (ui.state.latestVersionId) {
            api.extractDelayDriverCsv.instance.mutate(
                {
                    ...api.extractDelayDriverCsv.payload,
                    fileName: `${title}_${subTitle}`,
                },
                {
                    onSuccess: () => {
                        handleSnackBar('File Successfully Downloaded', 'success');
                        sendEventWithDimensions({
                            category: 'Export',
                            action: 'CSV',
                            label: `${title} ${subTitle}`,
                        });
                    },
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };

    const handleOnSearchSubmitted = (searchText: string) => {
        setOnSearch(searchText);
        table.setPagination({ ...ui.state.pagination, page: 1 });
        sendEventWithDimensions({
            category: 'Activities Table(s)',
            action: 'Search by Activity Name',
            label: searchText,
        });
    };

    const handleDate = useCallback(
        (value) => {
            setDateFilter(value);
            table.setPagination({ ...ui.state.pagination, page: 1 });
        },
        [ui.state.pagination],
    );

    const months = getMonths({
        startDate: api.getDelayedDrivers.data?.startDate,
        endDate: api.getDelayedDrivers.data?.finishDate,
    });

    const filteredRows: IActivitiesData[] = useMemo(
        () => api.getDelayedDrivers.data?.delayDrivers || [],
        [api.getDelayedDrivers.data],
    );

    const handleStatus = useCallback(
        (value) => {
            setTypeFilter(value);
            table.setPagination({ ...ui.state.pagination, page: 1 });
            sendEventWithDimensions({
                category: 'Activities Table(s)',
                action: 'Status Filter click',
                label: value.label || 'Deselect All',
            });
        },
        [ui.state.pagination],
    );

    const handleStatusType = useCallback(
        (value) => {
            table.setPagination({ ...ui.state.pagination, page: 1 });
            setStatusFilter(value);
        },
        [ui.state.pagination],
    );

    const handleCriticalPath = useCallback(
        (event) => {
            const isChecked = event.target.checked;
            table.setPagination({ ...ui.state.pagination, page: 1 });
            setIsCriticalPath(isChecked);
        },
        [ui.state.pagination],
    );

    const handleDelayDriver = useCallback(
        (event) => {
            const isChecked = event.target.checked;
            table.setPagination({ ...ui.state.pagination, page: 1 });
            setIsDelayDriver(isChecked);
        },
        [ui.state.pagination],
    );

    //  reset Pagination on sorting and new filter request
    useEffect(() => {
        table.setPagination({ ...ui.state.pagination, page: 1 });
    }, [
        ui.state.sorting,
        ui.state.onSearch,
        ui.state.wbs,
        ui.state.activityCodes,
        ui.state.milestoneFragnet,
        ui.state.tags,
    ]);

    // reset status & delay driver when global date has been changed
    useEffect(() => {
        handleDate(globalDateFilter);
        if (globalDateFilter.value !== 'All Months') {
            setIsDelayDriver(false);
            setStatusFilter([
                {
                    value: 'TODO',
                    label: 'To Do',
                },
                {
                    value: 'IN_PROGRESS',
                    label: 'In Progress',
                },
                {
                    value: 'Done',
                    label: 'Done',
                },
            ]);
            table.setPagination({ ...ui.state.pagination, page: 1 });
            handleSnackBar(`'Critical Activities' table filtered to ${globalDateFilter?.label}`, 'info');
        }
    }, [globalDateFilter]);

    useEffect(() => {
        if (api.getDelayedDrivers.data) {
            table.setTotalPages(api.getDelayedDrivers.data.numOfPages);
        }
    }, [api.getDelayedDrivers.data]);

    return {
        logic: {
            handleAPICsvExport,
            handleDate,
            months,
            filteredRows,
            handleStatus,
            handleStatusType,
            handleCriticalPath,
            handleDelayDriver,
            handleOnSearchSubmitted,
        },
    };
};
